import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { lazy, Suspense, useState } from "react";
import { Sparkles } from "lucide-react";
import {
  crmObtenerAprobacionPorToken,
  crmResolverAprobacionPorToken,
} from "@/server/crm/cotizaciones.fns";
import { formatSoles } from "@/lib/crm/calculos";
import type { CrmCotizacionCompleta } from "@/lib/crm/types";
import { formatHallazgoHace, type HallazgoIA } from "@/lib/cotizacion-ai-revision";
import { HallazgosIAList } from "@/components/crm/cotizaciones/HallazgosIAList";
import { MargenAprobacionBadge } from "@/components/crm/cotizaciones/MargenAprobacionBadge";

export const Route = createFileRoute("/aprobar-cotizacion")({
  validateSearch: (search) => ({ token: typeof search.token === "string" ? search.token : "" }),
  component: AprobarCotizacionPage,
});

const PREVIEW_WATERMARK = "VISTA PREVIA — SIN APROBAR";

// Mismo boundary lazy único que CotizacionPDFExportButton/CotizacionPDFPreviewDialog
// (panel admin) y por el mismo motivo: react-pdf reconcilia el `document` con su
// propio motor, así que no puede resolver como un lazy() separado sin arriesgar
// una carrera. Se define aparte (no se reutiliza el componente del panel) porque
// esta ruta vive en el Worker público y evita importar el bundle de admin — solo
// suma `@react-pdf/renderer` + `CotizacionPDF`, cargados bajo demanda al tocar el
// botón, nunca en la carga inicial de la página.
//
// Se usa PDFDownloadLink (no PDFViewer): PDFViewer renderiza un <iframe> con el
// visor propio de react-pdf, que no funciona en navegadores móviles. Un enlace de
// descarga sí — el propio buscador/sistema operativo se encarga de mostrar o
// guardar el PDF, que es exactamente lo que la mayoría abre desde el correo.
const VerPdfButton = lazy(async () => {
  const [{ PDFDownloadLink }, { CotizacionPDF }] = await Promise.all([
    import("@react-pdf/renderer"),
    import("@/components/crm/cotizaciones/CotizacionPDF"),
  ]);

  function VerPdfButtonInner({ cotizacion }: { cotizacion: CrmCotizacionCompleta }) {
    return (
      <PDFDownloadLink
        document={<CotizacionPDF cotizacion={cotizacion} watermark={PREVIEW_WATERMARK} />}
        fileName={`${cotizacion.numero}-v${cotizacion.version}-vista-previa.pdf`}
      >
        {({ loading }: { loading: boolean }) => (
          <span className="flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
            {loading ? "Generando PDF…" : "Ver PDF completo (vista previa)"}
          </span>
        )}
      </PDFDownloadLink>
    );
  }

  return { default: VerPdfButtonInner };
});

const MOTIVO_LABELS: Record<string, string> = {
  token_invalido: "El enlace no es válido.",
  ya_usado: "Este enlace ya fue utilizado.",
  estado_no_pendiente: "Esta cotización ya no está pendiente de aprobación.",
  expirado: "Este enlace venció. Pide que te reenvíen la cotización a aprobación.",
};

function Shell({ children, sidebar }: { children: React.ReactNode; sidebar?: React.ReactNode }) {
  return (
    <main className="min-h-screen bg-gray-50 flex items-start justify-center p-6">
      <div
        className={`w-full flex flex-col items-center gap-6 py-6 md:flex-row md:items-start md:justify-center ${sidebar ? "max-w-4xl" : "max-w-md"}`}
      >
        <div className="w-full max-w-md shrink-0 bg-white border rounded-lg p-6 space-y-4">
          <h1 className="text-xl font-semibold text-gray-900">Aprobación de cotización</h1>
          {children}
        </div>
        {sidebar}
      </div>
    </main>
  );
}

// Panel de solo lectura con los hallazgos de la revisión con IA (spec 114)
// que ya haya generado quien envió la cotización a aprobación — le da al
// aprobador el mismo contexto de análisis (consistencia, claridad, ítems
// desalineados entre opciones) sin poder aplicarlos/descartarlos desde acá:
// esa decisión ya se tomó (o quedó pendiente) en el editor del panel.
function RevisionIASidebar({
  hallazgos,
  generadoEn,
  partidas,
}: {
  hallazgos: HallazgoIA[];
  generadoEn: string | null;
  partidas: Array<{ nombre: string; items: Array<{ descripcion: string }> }>;
}) {
  const generadoHaceLabel = formatHallazgoHace(generadoEn);
  const pendientesCount = hallazgos.filter((h) => h.estado === "pendiente").length;

  return (
    <aside className="w-full max-w-md bg-white border rounded-lg p-4 space-y-3 md:w-80 md:sticky md:top-6">
      <div className="flex items-center gap-2">
        <Sparkles size={16} className="text-indigo-600" />
        <h2 className="text-sm font-semibold text-gray-900">Revisión con IA</h2>
      </div>
      <p className="text-xs text-gray-500">
        {generadoHaceLabel ? `Generada ${generadoHaceLabel}. ` : ""}
        {pendientesCount > 0
          ? `${pendientesCount} hallazgo(s) sin resolver por quien la envió.`
          : "Ya se resolvieron todos los hallazgos detectados."}
      </p>

      <HallazgosIAList hallazgos={hallazgos} partidas={partidas} />
    </aside>
  );
}

type Accion = "aprobar" | "rechazar";

function AprobarCotizacionPage() {
  const { token } = Route.useSearch();
  const [accion, setAccion] = useState<Accion | null>(null);
  const [justificacion, setJustificacion] = useState("");
  const [motivo, setMotivo] = useState("");

  const { data: info, isLoading } = useQuery({
    queryKey: ["aprobacion-por-token", token],
    queryFn: () => crmObtenerAprobacionPorToken({ data: { token } }),
    enabled: !!token,
  });

  const mutation = useMutation({
    mutationFn: () => {
      if (accion === "rechazar") {
        return crmResolverAprobacionPorToken({
          data: { token, accion: "rechazar", motivo },
        });
      }
      return crmResolverAprobacionPorToken({
        data: {
          token,
          accion: "aprobar",
          justificacion_coi: justificacion || undefined,
        },
      });
    },
  });

  if (!token) {
    return (
      <Shell>
        <p className="text-sm text-red-600">El enlace no contiene un token válido.</p>
      </Shell>
    );
  }

  if (isLoading) {
    return (
      <Shell>
        <p className="text-sm text-gray-500">Cargando…</p>
      </Shell>
    );
  }

  if (!info || !info.valido) {
    return (
      <Shell>
        <p className="text-sm text-red-600">
          {MOTIVO_LABELS[!info ? "token_invalido" : info.motivo]}
        </p>
      </Shell>
    );
  }

  if (mutation.isSuccess) {
    return (
      <Shell>
        <p className="text-sm text-green-700">
          {accion === "rechazar"
            ? "Cotización rechazada correctamente."
            : "Cotización aprobada correctamente."}
        </p>
      </Shell>
    );
  }

  const {
    cotizacion,
    cotizacionParaPdf,
    administradorNombre,
    puedeAprobar,
    puedeRechazar,
    requiereJustificacionCoi,
    hallazgosIA,
    revisionIAGeneradaEn,
  } = info;
  const requiereJustificacion = accion === "aprobar" && requiereJustificacionCoi;
  const requiereMotivo = accion === "rechazar";

  const puedeConfirmar =
    !!accion &&
    (!requiereJustificacion || justificacion.trim().length >= 20) &&
    (!requiereMotivo || motivo.trim().length >= 10);

  const sidebar =
    hallazgosIA !== null ? (
      <RevisionIASidebar
        hallazgos={hallazgosIA}
        generadoEn={revisionIAGeneradaEn}
        partidas={cotizacionParaPdf.partidas}
      />
    ) : undefined;

  return (
    <Shell sidebar={sidebar}>
      <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-900">
        Estás actuando como <span className="font-semibold">{administradorNombre}</span>.
      </div>

      <div className="rounded-md border border-gray-200 bg-gray-50 p-4 space-y-1.5 text-sm">
        <p>
          <span className="text-gray-500">Cotización:</span>{" "}
          <span className="font-medium text-gray-900">{cotizacion.numero}</span>
        </p>
        <p>
          <span className="text-gray-500">Evento:</span> {cotizacion.nombre_evento}
        </p>
        <p>
          <span className="text-gray-500">Cliente:</span> {cotizacion.empresa}
        </p>
        {cotizacion.monto.tipo === "exacto" ? (
          <p>
            <span className="text-gray-500">Monto total:</span>{" "}
            <span className="font-semibold">{formatSoles(cotizacion.monto.total)}</span>
          </p>
        ) : (
          // La cotización ofrece alternativas y el cliente aún no eligió: se
          // muestra el rango real en vez de un único número. Cada extremo ya
          // incluye el paquete base.
          <div>
            <p>
              <span className="text-gray-500">Monto total:</span>{" "}
              <span className="font-semibold">
                {formatSoles(cotizacion.monto.min)} a {formatSoles(cotizacion.monto.max)}
              </span>
            </p>
            <p className="text-xs text-gray-500">
              Esta cotización ofrece alternativas. El monto final depende de cuál elija el cliente;
              cada extremo ya incluye el paquete base.
            </p>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-gray-500">Margen:</span>
          <MargenAprobacionBadge
            margen={cotizacion.margen}
            minimo={cotizacion.margen_minimo}
            cumplimiento={cotizacion.cumplimiento_margen}
          />
          {cotizacion.cumplimiento_margen !== "cumple" && (
            <span className="text-xs text-gray-500">
              {cotizacion.cumplimiento_margen === "parcial"
                ? `alguna alternativa queda bajo el mínimo de ${cotizacion.margen_minimo}%`
                : `bajo el mínimo de ${cotizacion.margen_minimo}%`}
            </span>
          )}
        </div>
        {cotizacion.enviado_por_nombre && (
          <p>
            <span className="text-gray-500">Enviada por:</span> {cotizacion.enviado_por_nombre}
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <Suspense
          fallback={
            <span className="flex w-full items-center justify-center gap-2 rounded-md border border-gray-300 bg-white py-2.5 text-sm font-medium text-gray-400">
              Cargando vista previa…
            </span>
          }
        >
          <VerPdfButton cotizacion={cotizacionParaPdf} />
        </Suspense>
        <p className="text-xs text-gray-500">
          Revisa el detalle completo (ítems, precios, condiciones) antes de decidir. El PDF trae una
          marca de agua de vista previa — no es el documento final para el cliente.
        </p>
      </div>

      {!puedeAprobar && !puedeRechazar ? (
        <p className="text-sm text-gray-600">
          No puedes aprobar una cotización que tú mismo enviaste a aprobación. Pide a otro
          administrador activo que la revise desde el correo que también recibió.
        </p>
      ) : !accion ? (
        <div className="flex gap-2">
          {puedeAprobar && (
            <button
              type="button"
              onClick={() => setAccion("aprobar")}
              className="flex-1 rounded-md bg-emerald-600 text-white text-sm font-medium py-2 hover:bg-emerald-700"
            >
              Aprobar
            </button>
          )}
          {puedeRechazar && (
            <button
              type="button"
              onClick={() => setAccion("rechazar")}
              className="flex-1 rounded-md border border-red-300 text-red-700 text-sm font-medium py-2 hover:bg-red-50"
            >
              Rechazar
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {requiereJustificacion && (
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">
                Justificación (conflicto de interés, mín. 20 caracteres)
              </label>
              <textarea
                value={justificacion}
                onChange={(e) => setJustificacion(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-gray-300 text-sm px-2 py-1.5"
                placeholder="Eres el único administrador disponible — explica por qué procede la excepción…"
              />
            </div>
          )}

          {requiereMotivo && (
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">
                Motivo del rechazo (mín. 10 caracteres)
              </label>
              <textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                rows={3}
                className="w-full rounded-md border border-gray-300 text-sm px-2 py-1.5"
              />
            </div>
          )}

          {mutation.isError && (
            <p className="text-sm text-red-600">{(mutation.error as Error).message}</p>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setAccion(null)}
              disabled={mutation.isPending}
              className="flex-1 rounded-md border border-gray-300 text-gray-700 text-sm font-medium py-2 hover:bg-gray-50"
            >
              Volver
            </button>
            <button
              type="button"
              onClick={() => mutation.mutate()}
              disabled={!puedeConfirmar || mutation.isPending}
              className="flex-1 rounded-md bg-gray-900 text-white text-sm font-medium py-2 disabled:opacity-40 hover:bg-black"
            >
              {mutation.isPending
                ? "Confirmando…"
                : accion === "aprobar"
                  ? "Confirmar aprobación"
                  : "Confirmar rechazo"}
            </button>
          </div>
        </div>
      )}
    </Shell>
  );
}
